import { useState, useEffect, useRef } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import { FaGithub, FaLinkedin, FaEnvelope, FaMapMarkerAlt, FaPaperPlane, FaTerminal, FaCheck, FaTimes } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const sectionRef = useRef(null);
  const formRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });
  const controls = useAnimation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Terminal state
  const [terminalHistory, setTerminalHistory] = useState([
    { text: 'SYSTEM INTERACTION SHELL [v1.0.0]', type: 'system' },
    { text: 'ENTER "help" TO SEE AVAILABLE DIRECTIVES.', type: 'system' },
  ]);
  const [terminalInput, setTerminalInput] = useState('');
  const terminalBottomRef = useRef(null);

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration preserved exactly
      await emailjs.send(
        'service_g5jq24b', 
        'template_763pqtd', 
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        'QVolPtvzlKu-pb3mq' 
      );

      setSubmitStatus('success');
      // Trigger achievement on message sent
      const event = new CustomEvent('achievement-unlocked', { detail: 'messenger' });
      window.dispatchEvent(event);

      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 6000);
    }
  };

  // Terminal command handler
  const handleTerminalSubmit = (e) => {
    e.preventDefault();
    const cmd = terminalInput.trim().toLowerCase();
    if (!cmd) return;

    let response = [];
    if (cmd === 'help') {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: 'AVAILABLE DIRECTIVES:', type: 'output' },
        { text: '  whoami       - Profile overview', type: 'output' },
        { text: '  skills       - Skill mapping coordinates', type: 'output' },
        { text: '  stack        - Technical software stack', type: 'output' },
        { text: '  sudo hire-me - Request privileged contracting access', type: 'output' },
        { text: '  secret       - Reveal hidden developer sequence', type: 'output' },
        { text: '  clear        - Wipe console screen', type: 'output' },
      ];
    } else if (cmd === 'whoami') {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: 'SUBJECT: Jerwin Titus D', type: 'output' },
        { text: 'CREDENTIAL: B.Tech in Artificial Intelligence & Data Science', type: 'output' },
        { text: 'FOCUS: Computer Vision / Deep Learning / MLOps Infrastructure', type: 'output' },
        { text: 'STATUS: Open for selected contracts', type: 'output' },
      ];
    } else if (cmd === 'skills') {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: 'CORE TECHNOLOGY NODES:', type: 'output' },
        { text: '  ├── CV : YOLOv8, OpenCV, Tracking, Segmentation', type: 'output' },
        { text: '  ├── ML : PyTorch, TensorFlow, Scikit-learn, CNNs', type: 'output' },
        { text: '  └── DEV: FastAPI, React, Node.js, Docker, AWS', type: 'output' },
      ];
    } else if (cmd === 'stack') {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: 'ENGINE COMPILATION:', type: 'output' },
        { text: '  React 18 / Three.js / GSAP / Framer Motion / Tailwind CSS', type: 'output' },
      ];
    } else if (cmd === 'sudo hire-me') {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: '🔒 INITIATING PRIVILEGE ELEVATION...', type: 'output' },
        { text: '✓ ACCESS GRANTED. CONTRACT LOG LOADED.', type: 'output' },
        { text: 'ACTION REQUIRED: Submit the dispatch form on the left, or mail titusjerwin@gmail.com.', type: 'output' },
      ];
      // Trigger achievement for using sudo hire-me
      const event = new CustomEvent('achievement-unlocked', { detail: 'terminal-hack' });
      window.dispatchEvent(event);
    } else if (cmd === 'secret') {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: '🤫 DIAGNOSTIC TRIGGER SEQUENCE:', type: 'output' },
        { text: 'Type the sequence (up up down down left right left right b a) anywhere on this viewport to trigger vertex celebration.', type: 'output' },
      ];
    } else if (cmd === 'clear') {
      setTerminalHistory([]);
      setTerminalInput('');
      return;
    } else {
      response = [
        { text: `> ${terminalInput}`, type: 'input' },
        { text: `COMMAND NOT FOUND: "${cmd}". Enter "help" for options.`, type: 'error' },
      ];
    }

    setTerminalHistory((prev) => [...prev, ...response]);
    setTerminalInput('');
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (terminalBottomRef.current) {
      terminalBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [terminalHistory]);

  const contactInfo = [
    {
      icon: FaEnvelope,
      label: 'EMAIL',
      value: 'titusjerwin@gmail.com',
      link: 'mailto:titusjerwin@gmail.com',
    },
    {
      icon: FaGithub,
      label: 'GITHUB',
      value: 'JerwinTitus2006',
      link: 'https://github.com/JerwinTitus2006',
    },
    {
      icon: FaLinkedin,
      label: 'LINKEDIN',
      value: 'Jerwin Titus D',
      link: 'https://www.linkedin.com',
    },
    {
      icon: FaMapMarkerAlt,
      label: 'LOCATION',
      value: 'Coimbatore, India',
      link: null,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const renderBrackets = () => (
    <>
      <span className="reticle-bracket top-0 left-0 border-t border-l border-white-pure group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket top-0 right-0 border-t border-r border-white-pure group-hover:-translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 left-0 border-b border-l border-white-pure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 right-0 border-b border-r border-white-pure group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </>
  );

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="section-padding relative overflow-hidden bg-void border-t border-grey-800"
    >
      <div className="container-custom relative z-10">
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mb-20 text-left border-l-2 border-white-primary pl-6"
        >
          <motion.span
            variants={itemVariants}
            className="inline-block text-[9px] font-mono font-bold tracking-[0.35em] uppercase text-grey-400 mb-2"
          >
            06 // DATA_DISPATCH
          </motion.span>
          <motion.h2
            variants={itemVariants}
            className="text-3xl sm:text-5xl md:text-6xl font-display font-black"
          >
            CONTACT // <span className="chrome-text">SYSTEMS</span>
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch max-w-5xl mx-auto">
          {/* Left: Contact Form */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate={controls}
            className="flex flex-col"
          >
            <div className="bg-elevated border border-grey-800 p-8 lg:p-10 flex flex-col justify-between h-full relative group">
              {renderBrackets()}
              
              <h3 className="text-sm font-mono tracking-widest font-bold mb-6 flex items-center gap-3">
                <FaPaperPlane className="text-white-pure text-xs" />
                TRANSMIT MESSAGE
              </h3>

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-[9px] uppercase tracking-[0.2em] text-grey-400 font-mono font-semibold mb-2">
                    NAME // REQUIRED
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-void border border-grey-800 text-white-primary text-xs focus:border-white-pure focus:ring-1 focus:ring-white-pure transition-all duration-200 font-mono"
                    placeholder="ENTER NAME"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[9px] uppercase tracking-[0.2em] text-grey-400 font-mono font-semibold mb-2">
                    EMAIL // REQUIRED
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-void border border-grey-800 text-white-primary text-xs focus:border-white-pure focus:ring-1 focus:ring-white-pure transition-all duration-200 font-mono"
                    placeholder="ENTER EMAIL"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-[9px] uppercase tracking-[0.2em] text-grey-400 font-mono font-semibold mb-2">
                    SUBJECT // REQUIRED
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-void border border-grey-800 text-white-primary text-xs focus:border-white-pure focus:ring-1 focus:ring-white-pure transition-all duration-200 font-mono"
                    placeholder="ENTER SUBJECT"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[9px] uppercase tracking-[0.2em] text-grey-400 font-mono font-semibold mb-2">
                    MESSAGE // REQUIRED
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 bg-void border border-grey-800 text-white-primary text-xs focus:border-white-pure focus:ring-1 focus:ring-white-pure transition-all duration-200 resize-none font-mono"
                    placeholder="DESCRIBE INQUIRY SPECIFICATION..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-precision w-full disabled:opacity-50 group relative"
                >
                  <span className="flex items-center justify-center gap-2">
                    {isSubmitting ? 'TRANSMITTING...' : 'TRANSMIT MESSAGE'}
                    <FaPaperPlane className="text-xs" />
                  </span>
                </button>

                <AnimatePresence>
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="p-4 border border-white-pure bg-elevated text-white-pure text-[10px] font-mono font-semibold tracking-wider flex items-center justify-center gap-2"
                    >
                      <FaCheck className="text-xs" />
                      MESSAGE TRANSMITTED SUCCESSFULLY.
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="p-4 border border-grey-600 bg-elevated text-grey-400 text-[10px] font-mono font-semibold tracking-wider flex items-center justify-center gap-2"
                    >
                      <FaTimes className="text-xs" />
                      TRANSMISSION ERROR. SYSTEM OVERFLOW.
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </div>
          </motion.div>

          {/* Right: Interactive Terminal Emulator */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate={controls}
            className="flex flex-col"
          >
            <div className="border border-grey-800 bg-void flex flex-col h-full overflow-hidden min-h-[420px] relative group">
              {renderBrackets()}
              
              {/* Terminal Title Bar */}
              <div className="bg-elevated border-b border-grey-800 px-5 py-3.5 flex items-center justify-between select-none">
                <div className="flex items-center gap-2">
                  <FaTerminal className="text-xs text-white-pure" />
                  <span className="text-[10px] tracking-wider uppercase font-semibold text-grey-400 font-mono">NODE@JERWINTITUS: ~</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-grey-800" />
                  <div className="w-1.5 h-1.5 bg-grey-600" />
                  <div className="w-1.5 h-1.5 bg-white-pure" />
                </div>
              </div>

              {/* Terminal Logs Area */}
              <div className="flex-1 p-5 overflow-y-auto font-mono text-[11px] space-y-2 select-text text-grey-400">
                {terminalHistory.map((line, idx) => (
                  <div 
                    key={idx} 
                    className={
                      line.type === 'input' 
                        ? 'text-white-pure font-bold' 
                        : line.type === 'error' 
                        ? 'text-grey-400 font-bold border-l border-white-pure pl-2' 
                        : line.type === 'system'
                        ? 'text-grey-600 font-bold'
                        : 'text-white-primary'
                    }
                  >
                    {line.text}
                  </div>
                ))}
                <div ref={terminalBottomRef} />
              </div>

              {/* Terminal Form Input */}
              <form onSubmit={handleTerminalSubmit} className="border-t border-grey-800 flex items-center bg-elevated/40">
                <span className="pl-5 text-white-pure font-mono text-xs select-none">$</span>
                <input
                  type="text"
                  value={terminalInput}
                  onChange={(e) => setTerminalInput(e.target.value)}
                  className="flex-1 px-3 py-3.5 bg-transparent border-0 focus:ring-0 focus:outline-none font-mono text-xs text-white-pure placeholder-grey-600"
                  placeholder="type directive..."
                  autoComplete="off"
                  spellCheck="false"
                />
              </form>
            </div>
          </motion.div>
        </div>

        {/* Contact info nodes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mt-12">
          {contactInfo.map((item, index) => (
            <div key={index} className="group">
              {item.link ? (
                <a
                  href={item.link}
                  target={item.link.startsWith('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-elevated border border-grey-800 hover:border-grey-600 transition-all duration-300 relative"
                >
                  {renderBrackets()}
                  <div className="w-8 h-8 bg-void border border-grey-800 flex items-center justify-center flex-shrink-0 text-white-pure">
                    <item.icon className="text-xs" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-[8px] tracking-wider text-grey-600 uppercase font-mono font-semibold mb-0.5">{item.label}</p>
                    <p className="text-xs font-mono font-bold text-white-pure truncate">{item.value}</p>
                  </div>
                </a>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-elevated border border-grey-800 relative">
                  <div className="w-8 h-8 bg-void border border-grey-800 flex items-center justify-center flex-shrink-0 text-white-pure">
                    <item.icon className="text-xs" />
                  </div>
                  <div>
                    <p className="text-[8px] tracking-wider text-grey-600 uppercase font-mono font-semibold mb-0.5">{item.label}</p>
                    <p className="text-xs font-mono font-bold text-white-pure">{item.value}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Availability Badge */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-elevated border border-grey-800">
            <span className="w-1.5 h-1.5 bg-white-pure animate-pulse" />
            <span className="text-[9px] font-mono tracking-widest uppercase font-bold text-white-primary">SYSTEM STATUS // OPEN_FOR_CONTRACTS</span>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Contact;
